import { lazy, Suspense, useCallback, useState } from 'react';
import type { Area } from 'react-easy-crop';
import type { ImageCrop } from '@bazaar/shared';
import { Spinner } from '../Spinner.js';
import { BigButton } from '../BigButton.js';

// Dynamically imported so `react-easy-crop` never lands in the main bundle
// -- only a product-form visit that actually opens the cropper pulls it in.
const Cropper = lazy(() => import('react-easy-crop'));

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.1;

export interface PhotoCropperProps {
  /** The image to crop: an object URL for a freshly-picked file, or the product's originalUrl. */
  image: string;
  /** Pre-positions the crop area, e.g. from an existing product's stored `imageCrop`. */
  initialCrop?: ImageCrop | null;
  onCancel: () => void;
  onUse: (crop: ImageCrop) => void;
}

/**
 * "Position photo" dialog: lets the admin drag/zoom a square crop area over
 * the uploaded original so the product sits centred in every square
 * thumbnail the app shows. See the photo-crop spec, §4.
 */
export function PhotoCropper({ image, initialCrop, onCancel, onUse }: PhotoCropperProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);

  const handleCropComplete = useCallback((croppedArea: Area) => {
    setArea(croppedArea);
  }, []);

  function handleUse() {
    if (!area) return;
    onUse({
      x: area.x / 100,
      y: area.y / 100,
      w: area.width / 100,
      h: area.height / 100,
    });
  }

  function handleReset() {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink/40 sm:p-6"
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="photo-cropper-title"
        className="w-full h-[100dvh] sm:h-auto sm:max-w-lg bg-surface sm:rounded-2xl shadow-xl p-4 sm:p-6 flex flex-col gap-4 sm:max-h-[80vh] overflow-y-auto"
      >
        <div>
          <h2 id="photo-cropper-title" className="font-heading font-semibold text-lg text-ink">
            Position photo
          </h2>
          <p className="font-body text-base text-muted">
            Drag the photo so the product sits in the centre. Pinch or use the slider to zoom.
          </p>
        </div>

        <div className="relative w-full flex-1 sm:flex-none sm:aspect-square min-h-64 bg-teal-dark rounded-lg overflow-hidden">
          <Suspense
            fallback={
              <div className="h-full w-full flex items-center justify-center">
                <Spinner label="Loading cropper" />
              </div>
            }
          >
            <Cropper
              image={image}
              crop={crop}
              zoom={zoom}
              aspect={1}
              showGrid
              objectFit="contain"
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              initialCroppedAreaPercentages={
                initialCrop
                  ? { x: initialCrop.x * 100, y: initialCrop.y * 100, width: initialCrop.w * 100, height: initialCrop.h * 100 }
                  : undefined
              }
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCropComplete}
            />
          </Suspense>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Zoom out"
            onClick={() => setZoom((z) => Math.max(MIN_ZOOM, Math.round((z - ZOOM_STEP) * 100) / 100))}
            className="h-12 w-12 shrink-0 rounded-lg border-2 border-teal-dark text-teal-dark font-heading font-bold text-xl flex items-center justify-center"
          >
            &minus;
          </button>
          <label className="sr-only" htmlFor="photo-cropper-zoom">
            Zoom
          </label>
          <input
            id="photo-cropper-zoom"
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="flex-1 h-12 accent-teal-dark"
          />
          <button
            type="button"
            aria-label="Zoom in"
            onClick={() => setZoom((z) => Math.min(MAX_ZOOM, Math.round((z + ZOOM_STEP) * 100) / 100))}
            className="h-12 w-12 shrink-0 rounded-lg border-2 border-teal-dark text-teal-dark font-heading font-bold text-xl flex items-center justify-center"
          >
            +
          </button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <BigButton variant="outline" onClick={handleReset}>
            Reset
          </BigButton>
          <div className="btn-row">
            <BigButton variant="outline" onClick={onCancel}>
              Cancel
            </BigButton>
            <button
              type="button"
              onClick={handleUse}
              disabled={!area}
              className="min-h-12 px-6 rounded-lg font-heading font-bold text-xl bg-teal-dark text-white transition-opacity whitespace-nowrap hover:opacity-90 active:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Use photo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
